// src/pages/FeedbackPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import "../layout/MainLayout.css";

function FeedbackPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username") || "Misafir";
  const isLoggedIn = !!userId;

  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [showSupportOptions, setShowSupportOptions] = useState(false);

  // Auth check removed — page accessible to all

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!title.trim() || !description.trim()) {
      setError("Lütfen tüm alanları doldurun.");
      return;
    }

    try {
      const feedbackData = {
        userId,
        username,
        type: feedbackType,
        title: title.trim(),
        description: description.trim(),
        timestamp: new Date(),
        status: "pending",
      };

      await addDoc(collection(db, "feedbacks"), feedbackData);

      setSuccess(true);
      setTitle("");
      setDescription("");
      setFeedbackType("suggestion");

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Geri bildirim gönderilirken hata oluştu:", err);
      setError("Geri bildirim gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
    }
  };

  const toggleSupportOptions = () => {
    setShowSupportOptions((prev) => !prev);
  };

  return (
    <motion.div
      className="home-content"
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h2
        style={pageTitleStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        💡 Geri Bildirim ve Destek
      </motion.h2>
      <motion.div
        style={feedbackContainerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <motion.p
          style={introTextStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          Görüşleriniz bizim için çok değerli! Platformu daha iyi hale getirmek için önerilerinizi, hataları veya genel yorumlarınızı paylaşabilirsiniz. Ayrıca, destek almak için aşağıdaki seçenekleri kullanabilirsiniz.
        </motion.p>

        <AnimatePresence>
          {success && (
            <motion.div
              style={successMessageStyle}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
            >
              🎉 Geri bildiriminiz başarıyla gönderildi! Teşekkür ederiz.
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {error && (
            <motion.div
              style={errorMessageStyle}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.form
          onSubmit={handleSubmit}
          style={formStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <motion.div style={formGroupStyle}>
            <label htmlFor="feedbackType" style={labelStyle}>Geri Bildirim Türü:</label>
            <motion.select
              id="feedbackType"
              value={feedbackType}
              onChange={(e) => setFeedbackType(e.target.value)}
              style={selectStyle}
              whileFocus={{ borderColor: "#4CAF50" }}
              transition={{ duration: 0.2 }}
            >
              <option value="suggestion">Öneri</option>
              <option value="bug">Hata Bildirimi</option>
              <option value="general">Genel Yorum</option>
            </motion.select>
          </motion.div>

          <motion.div style={formGroupStyle}>
            <label htmlFor="title" style={labelStyle}>Başlık:</label>
            <motion.input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Geri bildiriminizin başlığını yazın..."
              style={inputStyle}
              whileFocus={{ borderColor: "#4CAF50" }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>

          <motion.div style={formGroupStyle}>
            <label htmlFor="description" style={labelStyle}>Açıklama:</label>
            <motion.textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detaylı açıklamanızı buraya yazın..."
              style={textareaStyle}
              whileFocus={{ borderColor: "#4CAF50" }}
              transition={{ duration: 0.2 }}
            />
          </motion.div>

          <motion.button
            type="submit"
            style={submitButtonStyle}
            whileHover={{ scale: 1.05, backgroundColor: "#388E3C" }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            Gönder
          </motion.button>
        </motion.form>

        <motion.div
          style={supportSectionStyle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <motion.button
            onClick={toggleSupportOptions}
            style={supportButtonStyle}
            whileHover={{ scale: 1.05, backgroundColor: "#388E3C" }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            Destek Al
          </motion.button>

          <AnimatePresence>
            {showSupportOptions && (
              <motion.div
                style={supportOptionsStyle}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.a
                  href="https://wa.me/+905442256334"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={supportOptionStyle}
                  whileHover={{ scale: 1.05, backgroundColor: "#388E3C" }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  📱 WhatsApp ile Destek
                </motion.a>
                <motion.a
                  href="https://www.instagram.com/direct/t/yelken.pro"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={supportOptionStyle}
                  whileHover={{ scale: 1.05, backgroundColor: "#388E3C" }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  📸 Instagram ile Destek
                </motion.a>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

const containerStyle = {
  background: "#121212", // Mat siyah arka plan
  minHeight: "100vh",
  padding: "40px",
  fontFamily: "'Inter', sans-serif",
  color: "#f1f1f1",
};

const pageTitleStyle = {
  fontSize: "24px",
  color: "#4CAF50", // Yeşil başlık
  marginBottom: "20px",
  textShadow: "0 0 4px rgba(76, 175, 80, 0.3)",
};

const feedbackContainerStyle = {
  background: "rgba(255, 255, 255, 0.05)",
  padding: "30px",
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(76, 175, 80, 0.2)", // Yeşil çerçeve
  maxWidth: "600px",
  margin: "0 auto",
};

const introTextStyle = {
  fontSize: "16px",
  color: "#b0bec5",
  marginBottom: "20px",
  textAlign: "center",
};

const successMessageStyle = {
  background: "#2e7d32", // Yeşil ton
  color: "#fff",
  padding: "15px",
  borderRadius: "8px",
  marginBottom: "20px",
  textAlign: "center",
  fontWeight: "bold",
};

const errorMessageStyle = {
  background: "#d32f2f",
  color: "#fff",
  padding: "15px",
  borderRadius: "8px",
  marginBottom: "20px",
  textAlign: "center",
  fontWeight: "bold",
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "15px",
};

const formGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "5px",
};

const labelStyle = {
  fontSize: "16px",
  color: "#4CAF50", // Yeşil etiket
};

const selectStyle = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#eee",
  fontSize: "16px",
};

const inputStyle = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#eee",
  fontSize: "16px",
};

const textareaStyle = {
  padding: "10px",
  borderRadius: "8px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#eee",
  fontSize: "16px",
  minHeight: "150px",
  resize: "vertical",
};

const submitButtonStyle = {
  padding: "12px 24px",
  background: "#4CAF50", // Yeşil buton
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "bold",
  cursor: "pointer",
};

const supportSectionStyle = {
  marginTop: "30px",
  textAlign: "center",
};

const supportButtonStyle = {
  padding: "12px 24px",
  background: "#4CAF50", // Yeşil buton
  border: "none",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "bold",
  cursor: "pointer",
};

const supportOptionsStyle = {
  marginTop: "15px",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const supportOptionStyle = {
  padding: "10px 20px",
  background: "#2a2a2a",
  borderRadius: "8px",
  color: "#fff",
  textDecoration: "none",
  fontSize: "14px",
  fontWeight: "bold",
  textAlign: "center",
};

export default FeedbackPage;